import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useWidget } from "@/context/widget-context";
import { Language, useTranslations } from "@/lib";
import { cn } from "@/lib/utils";
import { MoveRight } from "lucide-react";
import { ScrollArea } from "../ui";
export function SheetMenu({
  sheetOpen,
  setSheetOpen,
  children,
  sheetFooter,
  sheetTitle,
  sheetDescription,
  lang,
}: {
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  children: React.ReactNode;
  sheetFooter: React.ReactNode;
  sheetTitle?: React.ReactNode;
  sheetDescription?: React.ReactNode;
  lang: Language;
}) {
  const { products, step, setStep, setTrialRequestId } = useWidget();
  const { t } = useTranslations(lang);
  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <div
          className={cn(
            "tw:fixed tw:md:-right-18 tw:-right-17.5 tw:-rotate-90 tw:top-[calc(100vh_-_250px)] tw:md:top-[calc(100vh_-_325px)] tw:z-50 tw:cursor-pointer",
            lang === "ar" && "tw:-right-18 tw:md:-right-18",
            products.filter((product) => product.size).length === 0 &&
              "tw:hidden",
          )}
        >
          <Button
            className={cn(
              "tw:!px-3 tw:md:!px-5 tw:!py-px tw:md:py-5 tw:!h-6.3 tw:md:!h-10",
              lang === "ar" && "tw:flex-row-reverse",
            )}
          >
            <span className="tw:bg-[#e7e2d7] tw:text-xs tw:md:text-sm tw:rounded-full tw:w-[15px] tw:h-[15px] tw:md:w-5 tw:md:h-5">
              {" "}
              {products.filter((product) => product.size).length}
            </span>
            {t("floating_button.text")}{" "}
            <MoveRight className="tw:ml-1 tw:!inline" />
          </Button>
        </div>
      </SheetTrigger>
      <SheetContent
        handleClose={() => {
          // setStep(1);
          // setTrialRequestId(null);
          setSheetOpen(false);
        }}
        className={cn(
          "tw:[&:has(.hide-footer--btn)>[data-slot='sheet-footer']]:hidden tw:[&:has(.hide-header-btn)_[data-slot='sheet-header']]:p-0",
        )}
        tabIndex={undefined}
      >
        <div className="tw:min-h-2 tw:bg-primary">
          <div
            style={{
              width: `${(step / 7) * 100}%`,
            }}
            className={cn(
              "tw:h-2 tw:bg-[#809362] tw:transition-all tw:duration-500 tw:ease-in-out tw:rounded-r",
              step === 7 && "tw:rounded-r-none",
            )}
          >
            &nbsp;
          </div>
        </div>
        <ScrollArea
          className={cn(
            step === 1 && "tw:h-[calc(100dvh_-_96px)] tw:md:h-[88dvh]",
            step === 2 && "tw:h-[calc(100dvh_-_256px)] tw:md:h-[75dvh]",
            step === 4 && "tw:h-[calc(100dvh_-_96px)] tw:md:h-[88dvh]",
            step === 5 && "tw:h-screen tw:relative",
            step === 6 && "tw:h-[100dvh] tw:md:h-[90dvh]",
            step === 7 && "tw:h-auto",
          )}
        >
          <SheetHeader>
            <SheetTitle className="tw:font-pp-fragment tw:!text-xl tw:!font-bold tw:!mb-2">
              {sheetTitle || t("header_content.title")}
            </SheetTitle>
            <SheetDescription className="tw:text-base tw:font-pp-fragment tw:!mb-3">
              {sheetDescription || (
                <>
                  {t("header_content.subtitle")}
                  <br />
                  <span className="tw:font-karla">
                    {t("header_content.description")}
                  </span>
                </>
              )}
            </SheetDescription>
          </SheetHeader>

          {children}
        </ScrollArea>

        <SheetFooter className="tw:!border-t">{sheetFooter}</SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
